import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useT } from "../i18n";
import LangSwitcher from "../components/LangSwitcher";
import { DownloadSimple, Clock, FilmStrip, Image as ImageIcon, ShieldWarning } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function bytesToHuman(b) {
    if (!b && b !== 0) return "-";
    const u = ["B", "KB", "MB", "GB", "TB"];
    let i = 0;
    let n = b;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n >= 100 || i === 0 ? 0 : 1)} ${u[i]}`;
}

function formatLeft(iso) {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return "—";
    const h = Math.floor(ms / 3600_000);
    const m = Math.floor((ms % 3600_000) / 60_000);
    if (h >= 24) return `${Math.floor(h / 24)}z ${h % 24}h`;
    if (h >= 1) return `${h}h ${m}m`;
    return `${m}m`;
}

export default function SharePublic() {
    const { token } = useParams();
    const { t } = useT();
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        axios
            .get(`${API}/share/${token}/meta`)
            .then((r) => setMeta(r.data))
            .catch((e) => setError(e.response?.data?.detail || t("share_pub_default_err")));
    }, [token, t]);

    const fileUrl = `${API}/share/${token}/file`;
    const downloadUrl = `${API}/share/${token}/download`;

    return (
        <div className="min-h-screen flex flex-col" style={{ background: "var(--bg)", color: "var(--text)" }}>
            <div className="grain" />
            <header className="border-b" style={{ borderColor: "var(--border)" }}>
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <div className="label-mono">{t("share_pub_brand")}</div>
                        <div className="heading-tight text-lg sm:text-xl">{t("share_pub_h1")}</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <LangSwitcher compact />
                        {meta && (
                            <div
                                className="flex items-center gap-2 px-2.5 py-1 border"
                                style={{ borderColor: "var(--border)" }}
                                data-testid="share-countdown"
                            >
                                <Clock size={14} />
                                <span className="label-mono">{t("share_expires_in")} {formatLeft(meta.expires_at)}</span>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main className="flex-1 relative z-10 max-w-5xl w-full mx-auto px-4 sm:px-6 py-6">
                {error ? (
                    <div
                        className="border p-8 sm:p-12 text-center max-w-lg mx-auto"
                        style={{ borderColor: "var(--danger)" }}
                        data-testid="share-error"
                    >
                        <ShieldWarning size={48} className="mx-auto mb-4" style={{ color: "var(--danger)" }} />
                        <div className="heading-tight text-2xl mb-2">{t("share_pub_denied")}</div>
                        <div className="text-sm" style={{ color: "var(--text-muted)" }}>{String(error)}</div>
                    </div>
                ) : !meta ? (
                    <div className="aspect-video tile-shimmer" />
                ) : (
                    <div className="space-y-4">
                        <div className="border" style={{ borderColor: "var(--border)", background: "#000" }}>
                            {meta.media.kind === "image" ? (
                                <img src={fileUrl} alt={meta.media.original_name} className="w-full max-h-[78vh] object-contain" data-testid="share-image" />
                            ) : (
                                <video src={fileUrl} controls playsInline className="w-full max-h-[78vh] bg-black" data-testid="share-video" />
                            )}
                        </div>
                        <div className="border p-4 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                            <div className="min-w-0 flex items-center gap-3">
                                {meta.media.kind === "image" ? <ImageIcon size={20} /> : <FilmStrip size={20} />}
                                <div className="min-w-0">
                                    <div className="text-sm truncate" data-testid="share-filename">{meta.media.original_name}</div>
                                    <div className="label-mono">{bytesToHuman(meta.media.size)} · {meta.media.mime}</div>
                                </div>
                            </div>
                            <a
                                href={downloadUrl}
                                download={meta.media.original_name}
                                data-testid="btn-share-download"
                                className="btn-press h-11 px-4 flex items-center gap-2 text-sm"
                                style={{ background: "var(--text)", color: "var(--bg)" }}
                            >
                                <DownloadSimple size={18} /> {t("share_pub_dl")}
                            </a>
                        </div>
                    </div>
                )}
            </main>

            <footer className="py-4 text-center label-mono" style={{ color: "var(--text-dim)" }}>
                {t("share_pub_footer")}
            </footer>
        </div>
    );
}
